/**
 * AI の回答用の簡易 Markdown 表示
 *
 * Markdown ライブラリを導入していないため、見出し・箇条書き・番号付きリスト・段落・太字だけを解釈する。
 */
import { Fragment } from 'react'

import { cn } from '@/lib/utils'

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'paragraph'; text: string }

const HEADING_RE = /^#{1,4}\s+(.*)$/
const BULLET_RE = /^\s*[-*・]\s+(.*)$/
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/
const BOLD_RE = /\*\*(.+?)\*\*/

const parseBlocks = (markdown: string): Block[] => {
  const blocks: Block[] = []
  let list: { ordered: boolean; items: string[] } | null = null
  let paragraph: string[] = []

  const flushList = () => {
    if (list) blocks.push({ kind: 'list', ...list })
    list = null
  }
  const flushParagraph = () => {
    if (paragraph.length > 0) blocks.push({ kind: 'paragraph', text: paragraph.join('\n') })
    paragraph = []
  }

  for (const rawLine of markdown.split('\n')) {
    const line = rawLine.trimEnd()
    const heading = HEADING_RE.exec(line)
    const bullet = BULLET_RE.exec(line)
    const ordered = ORDERED_RE.exec(line)

    if (heading) {
      flushList()
      flushParagraph()
      blocks.push({ kind: 'heading', text: heading[1] ?? '' })
    } else if (bullet || ordered) {
      flushParagraph()
      const isOrdered = !bullet
      if (list && list.ordered !== isOrdered) flushList()
      list ??= { ordered: isOrdered, items: [] }
      list.items.push((bullet ?? ordered)?.[1] ?? '')
    } else if (line.trim() === '') {
      flushList()
      flushParagraph()
    } else {
      flushList()
      paragraph.push(line)
    }
  }
  flushList()
  flushParagraph()
  return blocks
}

/** **太字** を <strong> にする */
const Inline = ({ text }: { text: string }) => {
  const parts = text.split(BOLD_RE)
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold">
            {part}
          </strong>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  )
}

export const SimpleMarkdown = ({ text, className }: { text: string; className?: string }) => {
  const blocks = parseBlocks(text)
  return (
    <div className={cn('space-y-2 text-sm leading-relaxed', className)}>
      {blocks.map((block, i) => {
        if (block.kind === 'heading') {
          return (
            <p key={i} className="font-semibold">
              <Inline text={block.text} />
            </p>
          )
        }
        if (block.kind === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul'
          return (
            <ListTag key={i} className={cn('space-y-1 pl-5', block.ordered ? 'list-decimal' : 'list-disc')}>
              {block.items.map((item, j) => (
                <li key={j} className="wrap-break-word">
                  <Inline text={item} />
                </li>
              ))}
            </ListTag>
          )
        }
        return (
          <p key={i} className="wrap-break-word whitespace-pre-wrap">
            <Inline text={block.text} />
          </p>
        )
      })}
    </div>
  )
}
